import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import MenuItem from "@material-ui/core/MenuItem";
import _ from "lodash";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { ILocation } from "types/cart/shipment-review";
import { postToTopIframe, postToWindow } from "helpers/window-messages";
import { IGridContext } from "components/shipment-review/types";
import { handleDuplicateLocationsModal, updateLineItem, updateWorkingOrder } from "actions/cart-legacy";
import { format } from "lib/globalize-util";
import MoreMenu from "./MoreMenu";
import { IStore } from "reducers";
import { getGridBuffer } from "../helpers/copyPasteHandlers";
import { BufferContext, GridBuffer } from "constants/sizingView";
import { getFilteredLeafs, isAllLocationCombinedGroup } from "../helpers/sizingViewHelper";
import { AllocationType, IWorkingOrder } from "types/cart";
import { DATATEST } from "constants/integration";

type IOwnProps = CastProp<CastProp<ICellRendererParams, "context", IGridContext>, "data", IGridItem>;

interface IStateProps {
    locations: ILocation[];
    order: IWorkingOrder;
}

const LocationMenu: FC<IOwnProps & IStateProps> = (props) => {
    const { locations, node, order } = props;

    const hasCombined = useMemo(() => _.some(locations, { id: null }), [locations]);
    const hasByLocation = useMemo(() => _.some(locations, "id"), [locations]);
    const otherLocations = useMemo(
        () => _.filter(locations, ({ id, name }) => !_.isNull(id) && !_.isEqual(_.toUpper(name), node.key)),
        [node.key, locations],
    );
    const childrenNodes = useMemo(() => getFilteredLeafs(node), [node]);
    const showDuplicateQTY = useMemo(() => !isAllLocationCombinedGroup(node), [node]);

    const rowsOptions = useMemo(
        () => ({
            hasCombined: _.some(childrenNodes, (n) => n.data.options.hasCombined),
            hasLocations: _.some(childrenNodes, (n) => n.data.options.hasLocations),
        }),
        // should update when order updates to track changes in rows
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [childrenNodes, order],
    );

    const changeAllocation = useCallback(
        (allocated) => {
            const ids = _.uniq(_.flatMap(childrenNodes, (n) => _.map(n.data.entries, "cartLineItemId")));
            // TODO: should support build order
            postToWindow(updateWorkingOrder(_.map(ids, (id) => updateLineItem(id, { allocated }))));
        },
        [childrenNodes],
    );

    const enableAllLocationsCombine = useCallback(() => changeAllocation(AllocationType.Combined), [changeAllocation]);
    const enableByLocations = useCallback(() => changeAllocation(AllocationType.ByLocation), [changeAllocation]);

    const openDuplicateLocationsModal = useCallback(() => {
        const buffer: GridBuffer = getGridBuffer<IGridItem>(BufferContext.Row, {
            rows: getGroupData(node),
            columns: [],
        });
        // get destination entries
        const data: IGridItem[] = _.flatMap(node.parent.childrenAfterFilter, (n: RowNode) =>
            // except current location and "All locations combined"
            n.key === node.key || isAllLocationCombinedGroup(n) ? [] : getGroupData(n),
        );

        postToTopIframe(handleDuplicateLocationsModal(otherLocations, buffer, data));
    }, [otherLocations, node]);

    return (
        <MoreMenu closeOnClick>
            <MenuItem
                onClick={enableAllLocationsCombine}
                disabled={!rowsOptions.hasLocations || !hasCombined}
                data-test={DATATEST.GRID_VIEW.LOCATIONS_MENU.ENABLE_ALL_LOCATIONS}
            >
                {format("Enable All Locations Combined")}
            </MenuItem>
            <MenuItem
                onClick={enableByLocations}
                disabled={!rowsOptions.hasCombined || !hasByLocation}
                data-test={DATATEST.GRID_VIEW.LOCATIONS_MENU.ENABLE_BY_LOCATION}
            >
                {format("Enable By Location")}
            </MenuItem>
            {showDuplicateQTY && (
                <MenuItem
                    onClick={openDuplicateLocationsModal}
                    disabled={otherLocations.length < 1}
                    data-test={
                        node.level === 0
                            ? DATATEST.GRID_VIEW.LOCATIONS_MENU.DUPLICATE_QUANTITIES
                            : DATATEST.GRID_VIEW.LOCATIONS_MENU.DUPLICATE_QUANTITIES_IN_GROUP
                    }
                >
                    {node.level === 0 ? format("Duplicate quantities") : format("Duplicate quantities in group")}
                </MenuItem>
            )}
        </MoreMenu>
    );
};

const getGroupData = (node: RowNode) => _.map(getFilteredLeafs(node), "data");

const mapStateToProps = ({ cartGridView }: IStore): IStateProps => ({
    locations: cartGridView.data.locations,
    order: cartGridView.data.order,
});

export default connect<IStateProps, {}, IOwnProps>(mapStateToProps)(LocationMenu);
